import { getProductDetailHref } from "@/libs/product/detail-href";
import { useState } from "react";
import { useRouter } from "next/router";
import { useTrans } from "@/utils/useTrans";
import { ProductUtil } from "@/utils/ProductUtil";
import { cn } from "@/libs/utils";
import { Button, buttonVariants } from "@/v2-components/ui/button";
import {
  useProducts,
  useGetBundleProducts,
} from "@/services/product/useProductService";
import { useAddCartStoreItem } from "@/store/cart-store";
import { useAddedToBagStore } from "@/store/cart-store/useAddedToBagStore";
import { NotifyMe } from "@/v2-components/pages/tactsuit/NotifyMe";
import { SkuOptionsDialog } from "@/v2-components/cart-sheet/sku-options-dialog";
import { useTactSuitCardContext } from "@/v2-components/tact-suit-card-context";
import { type VariantProps } from "class-variance-authority";

export function NotifyOrBuy({
  className,
  variant = "blue-outline",
  label = "Buy Now",
  onClick,
  children,
  allowBundleSuggestion = true,
  showSkuOptionsDialog = false,
}: React.PropsWithChildren<{
  label?: string;
  className?: string;
  variant?: VariantProps<typeof buttonVariants>["variant"];
  onClick?: (e: any) => void;
  allowBundleSuggestion?: boolean;
  showSkuOptionsDialog?: boolean;
}>) {
  const { t, tp } = useTrans(["tactsuit"]);
  const { product, sku } = useTactSuitCardContext();
  const { data: products } = useProducts();
  const [isSkuOptionsDialogOpen, setIsSkuOptionsDialogOpen] = useState(false);
  const bundleProducts = useGetBundleProducts(product);
  const router = useRouter();
  const translatedProduct = tp(product);
  const sellableProducts = [product, ...bundleProducts].filter(
    (p) => !ProductUtil.isProductSoldOut(p),
  );
  const addCartItem = useAddCartStoreItem();
  const openAddedToBag = useAddedToBagStore((s) => s.open);

  const isSoldOut = ProductUtil.isProductSoldOut(
    products?.find((p) => p.slug === product.slug),
  );
  if (isSoldOut) {
    if (
      !allowBundleSuggestion ||
      (allowBundleSuggestion && sellableProducts.length === 0)
    ) {
      return (
        <NotifyMe
          productSku={sku}
          className={cn("px-6 text-coolgray-90", className)}
          productSlug={product.slug}
        />
      );
    }
  }

  return (
    <>
      <Button
        onClick={(e) => {
          /**
           * 이 갈래는 **액세서리 카드의 담기**다. 소비처는 2곳이다 (2026-09-03 실측):
           *
           * ```
           * src/pages/tactsuit/accessories.tsx:156        액세서리 목록
           * src/pages/shop/buy-tactsuit/index.tsx:298     기기 담은 뒤 업셀 섹션
           * ```
           *
           * ⚠️ 종전 주석은 *"accessories 페이지 전용"* 이었는데 **거짓**이었다 —
           * `grep -rn showSkuOptionsDialog src` 로 세면 두 자리다.
           */
          if (showSkuOptionsDialog) {
            const skuOptionsCount = product.skuOptions?.length || 0;
            // skuOptions가 1개 이하인 경우 바로 카트에 추가
            if (skuOptionsCount <= 1) {
              const firstSkuOption = product.skuOptions?.[0];
              if (
                firstSkuOption &&
                firstSkuOption.sku &&
                firstSkuOption.options
              ) {
                const { added, merged } = addCartItem(
                  {
                    slug: product.slug,
                    quantity: 1,
                    sku: firstSkuOption.sku,
                    options:
                      ProductUtil.getOptionsFromSkuOption(firstSkuOption),
                  },
                  // 목록·카드에서 담기 (#5292)
                  "product_card",
                );
                /**
                 * ★담기 완료 팝을 띄운다 — 종전에는 **`/cart` 로 이동**했다 (#5480).
                 *
                 * 원인은 이 자리가 아니라 `showCart()` 의 의미가 바뀐 것이다: 카트가
                 * 시트 → 페이지로 옮겨가면서(#5081) `showCart()` 가
                 * `Router.push("/cart")` 가 됐다(`cart-store/index.ts:89-96`). 그래서
                 * 「담기」가 조용히 「이동」이 됐고, 장갑 카드(`NotifyOrAddButton` →
                 * `useAddItemToCart`)만 팝을 띄우는 비대칭이 남았다.
                 *
                 * 실측(2026-09-03 · localhost:3000 · dev):
                 *
                 * ```
                 * /ko/tactsuit/accessories/                     담기 → /ko/cart/  (dialog 0)
                 * /ko/shop/buy-tactsuit/?product=tactsuit-pro   담기 → /ko/cart/  (dialog 0)
                 * ```
                 *
                 * ⚠️ **`added` 로 잠근다.** `useAddCartStoreItem` 의 반환은 두 갈래뿐이고
                 * (`cart-store/index.ts:587-610`) 담기지 않는 갈래는 수량 상한
                 * (`isOverItemLimit`)이다 — 거기서 팝을 띄우면 **담기지 않았는데
                 * "담았다"** 가 되고 Purchase Limit 안내와 겹친다(#5366 이 같은 클래스를
                 * `useAddItemToCart` 에서 이미 겪었다).
                 *
                 * ⚠️ **`merged` 도 본다**(#5945 R1). `addDeviceItem` 자체는 번들 병합을 안 하지만, 그 뒤
                 * `packCartSets` 가 수량 세트를 접는다(MOLLE Pouch 단품 2개 → 세트 1개). 접히면 방금
                 * 담은 SKU 줄이 사라져 팝이 카드 0장으로 뜬다 — 그때는 훅이 이미 병합 다이얼로그를
                 * 열었으므로 팝을 띄우지 않는다.
                 */
                if (added && !merged) {
                  openAddedToBag(firstSkuOption.sku);
                }
              }
              onClick?.(e);
              return;
            }
            // skuOptions가 2개 이상인 경우 다이얼로그 표시
            setIsSkuOptionsDialogOpen(true);
            return;
          }

          // 번들 업그레이드는 구매 상세 페이지 내에서 선택하므로 다이얼로그 없이 바로 이동
          router.push(getProductDetailHref(product.slug));
          onClick?.(e);
        }}
        size={"sm"}
        shape={"pill"}
        variant={variant}
        className={cn("px-6", className)}
      >
        {t(label, { ns: "gnb" })}
        {!!children && children}
      </Button>
      <SkuOptionsDialog
        open={isSkuOptionsDialogOpen}
        product={translatedProduct}
        onClose={() => setIsSkuOptionsDialogOpen(false)}
      />
    </>
  );
}
